import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    FormControlLabel,
    Tabs,
    Tab,
    Typography,
    SvgIcon,
    CircularProgress
} from '@mui/material';
import { FileText, CircleAlert, CircleCheck, X } from 'lucide-react-new';
import { useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import tosDocument from 'src/views/reports/TermsOfService/Foundry_USA_Pool_Service_Agreement.pdf';
import { makeStyles } from 'tss-react/mui';
import { useSnackbar } from 'notistack';
import useAuth from 'src/hooks/useAuth';
import AcceptTosVersion from 'src/api/AcceptTosVersion';
import { CURRENT_TOS_VERSION, PDFJS_WORKER_VERSION } from 'src/constants';
import { isTosValid, getOwnerRoleGroups } from 'src/utils/tosValidation';

// Configure PDF.js worker - use local copy to avoid CDN dependency
// Version in filename ensures proper cache invalidation on updates
pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.${PDFJS_WORKER_VERSION}.min.mjs`;

const useStyles = makeStyles()(theme => ({
    reportFilterButton: {
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        color: 'var(--accent-9-contrast) !important',
        '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'var(--color-surface-raised)'
        },
        boxShadow: '0 0 0 0 transparent',
        textTransform: 'none',
        padding: 'var(--spacing-md, 8px) var(--spacing-xl, 16px)',
        minHeight: '40px !important'
    },
    selectedTab: {
        '& .Mui-selected': {
            backgroundColor: 'var(--Colors-Background-bg-active, #1F242F);',
        },
        alignItems: 'center !important',
        marginRight: 8,
        '& .MuiTab-root': {
            color: 'var(--accent-9-contrast)'
        }
    }
}));

const TermsOfServiceDialog = ({ open, onAllAccepted, isDismissable = false, onDismiss }) => {
    const { loggedInUser, getAccessToken, logout } = useAuth();
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    // Non-dismissable for non-admin owners: they can only leave by logging out.
    // Admins can dismiss instead (close button / backdrop / Esc — see isDismissable).
    // Mirrors the logout flow in TopBar/Account.js.
    const handleLogout = async () => {
        try {
            sessionStorage.clear();
            await logout();
            navigate('/');
        } catch (err) {
            enqueueSnackbar('Unable to log out. Please try again.', {
                variant: 'error',
            });
        }
    };
    const [boxChecked, setBoxChecked] = useState(new Set())
    const [agreedToTerms, setAgreedToTerms] = useState(new Set())

    // Acceptance is recorded per-group and binds the whole organization, so only a user
    // who holds the Group Owner role on a group can accept for it. The dialog therefore
    // only ever deals with the groups where this user has that role — users without it
    // never reach it (gated out in DashboardLayout) and their other groups are irrelevant
    // here. ownerRoleGroups is the single source of truth for every group list in this
    // dialog (tabs, initial tab, next-tab routing, stale count); it shares the same
    // selector DashboardLayout's gate uses, so the gate and the dialog can't drift.
    const ownerRoleGroups = getOwnerRoleGroups(loggedInUser);

    // Land on the first Group Owner group that still needs acceptance; fall back to
    // the first such group if somehow none are stale.
    const getInitialTab = () => {
        const firstStale = ownerRoleGroups.find(groupItem => !isTosValid(groupItem.group.accepted_tos_version));
        return (firstStale || ownerRoleGroups[0])?.group.name || null;
    };

    const [selectedTab, setSelectedTab] = useState(getInitialTab());
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(new Set());
    const [numPages, setNumPages] = useState(null);
    const calcPdfWidth = () => window.innerWidth < 600
        ? window.innerWidth - 80
        : Math.min(window.innerWidth * 0.6, 800);
    const [pdfWidth, setPdfWidth] = useState(calcPdfWidth);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const contentRef = useRef(null);
    const tabsRef = useRef(null);
    const hasScrolledRef = useRef(new Set());

    const pdfOptions = React.useMemo(() => ({
        enableXfa: false,
    }), []);

    const pdfFile = React.useMemo(() => ({ url: tosDocument }), []);

    const onDocumentLoadSuccess = React.useCallback(({ numPages: loadedPages }) => {
        setNumPages(loadedPages);
    }, []);

    const onDocumentLoadError = React.useCallback((error) => {
        enqueueSnackbar('Failed to load the Terms of Service document. Please refresh the page.', {
            variant: 'error',
        });
    }, [enqueueSnackbar]);

    const processPageLinks = React.useCallback((pageNumber) => {
        if (!contentRef.current) return;
        const pageEl = contentRef.current.querySelector(`.react-pdf__Page[data-page-number="${pageNumber}"]`)
            || contentRef.current.querySelectorAll('.react-pdf__Page')[pageNumber - 1];
        if (!pageEl) return;

        const spans = Array.from(pageEl.querySelectorAll('.textLayer span, .react-pdf__Page__textContent span'));

        for (let i = 0; i < spans.length; i++) {
            const text = spans[i].textContent;
            const urlMatch = text.match(/(https?:\/\/\S+)/);
            if (!urlMatch) continue;

            const urlRaw = urlMatch[1];
            const urlStart = text.indexOf(urlRaw);
            let urlPart = urlRaw.replace(/[.,;:!?)\]'"]+$/, '');

            // If the URL is preceded by other text in the same span, split it so
            // only the URL portion gets styled (not the surrounding sentence text).
            let firstLinkSpan = spans[i];
            if (urlStart > 0) {
                const inner = document.createElement('span');
                inner.textContent = text.substring(urlStart);
                spans[i].textContent = text.substring(0, urlStart);
                spans[i].appendChild(inner);
                firstLinkSpan = inner;
                urlPart = inner.textContent.replace(/[.,;:!?)\]'"]+$/, '');
            }

            const involved = [firstLinkSpan];

            let j = i + 1;
            while (j < spans.length) {
                const t = spans[j].textContent.trim();
                if (t === '-' && j + 1 < spans.length) {
                    const after = spans[j + 1].textContent.trim();
                    if (/^[a-zA-Z0-9]/.test(after)) {
                        urlPart = urlPart + '-' + after.replace(/[.,;:!?)\]'"]+$/, '');
                        involved.push(spans[j], spans[j + 1]);
                        j += 2;
                    } else {
                        break;
                    }
                } else if (urlPart.endsWith('-') && t) {
                    urlPart = urlPart + t.replace(/[.,;:!?)\]'"]+$/, '');
                    involved.push(spans[j]);
                    j++;
                } else {
                    break;
                }
            }

            involved.forEach(s => {
                s.style.cursor = 'pointer';
                s.style.textDecorationLine = 'underline';
                s.style.textDecorationColor = 'var(--color-text-success)';
                s.onclick = () => window.open(urlPart, '_blank', 'noopener,noreferrer');
            });
        }
    }, []);

    const handleTabsChange = (_event, value) => {
        setSelectedTab(value);

        if (!agreedToTerms.has(value)) {
            hasScrolledRef.current.delete(value);
            setHasScrolledToBottom(prevSet => {
                const newSet = new Set(prevSet);
                newSet.delete(value);
                return newSet;
            });

            setBoxChecked(prevSet => {
                const newSet = new Set(prevSet);
                newSet.delete(value);
                return newSet;
            });
        } else {
            setBoxChecked(prevSet => {
                const newSet = new Set(prevSet);
                newSet.add(value);
                return newSet;
            });
        }

        scrollToTop();
    };

    const handleToggleSet = (value) => {
        setBoxChecked(prevSet => {
            const newSet = new Set(prevSet);
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }
            return newSet;
        });
    };

    const handleAgreeClick = async (value) => {
        // Prevent double-clicks
        if (isSubmitting) return;

        // Find the current group to get the group ID
        const currentGroup = loggedInUser.groups.find(g => g.group.name === value);
        if (!currentGroup) return;

        setIsSubmitting(true);

        try {
            // Get access token
            const { tokenAvailable, accessToken } = await getAccessToken();
            if (!tokenAvailable) {
                enqueueSnackbar('Unable to accept Terms of Service. Please try logging in again.', {
                    variant: 'error',
                });
                return;
            }

            // Make API call to accept TOS
            const result = await AcceptTosVersion(currentGroup.group.id, CURRENT_TOS_VERSION, accessToken);

            if (!result.success) {
                enqueueSnackbar(`Failed to accept Terms of Service: ${result.data || 'Please try again.'}`, {
                    variant: 'error',
                });
                return;
            }

            // Calculate if we should close BEFORE updating any state
            const newAgreedSet = new Set(agreedToTerms);
            newAgreedSet.add(value);

            // Find the next Group Owner group that still needs acceptance
            const nextTab = ownerRoleGroups.find(groupItem => {
                const tosIsValid = isTosValid(groupItem.group.accepted_tos_version);
                const needsAcceptance = !tosIsValid && !newAgreedSet.has(groupItem.group.name);
                return needsAcceptance;
            });

            if (nextTab) {
                // Update state and move to next tab that needs acceptance
                setAgreedToTerms(newAgreedSet);
                setSelectedTab(nextTab.group.name);
                hasScrolledRef.current.delete(nextTab.group.name);
                setHasScrolledToBottom(prevScrollSet => {
                    const newScrollSet = new Set(prevScrollSet);
                    newScrollSet.delete(nextTab.group.name);
                    return newScrollSet;
                });
                setBoxChecked(prevCheckSet => {
                    const newCheckSet = new Set(prevCheckSet);
                    newCheckSet.delete(nextTab.group.name);
                    return newCheckSet;
                });
                scrollToTop();
            } else {
                // All required tabs are agreed — let the parent close the dialog
                // WITHOUT updating state here (prevents state updates after we've
                // decided to close). This is the only path that dismisses the dialog.
                onAllAccepted();
            }
        } catch (error) {
            enqueueSnackbar('An error occurred while accepting the Terms of Service. Please try again.', {
                variant: 'error',
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    const scrollToTop = () => {
        if (contentRef.current) {
            contentRef.current.scrollTo({
                top: 0,
                behavior: 'instant'
            });
        }
    };

    const handleScroll = (e) => {
        const element = e.target;
        const bottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;

        if (bottom && !hasScrolledRef.current.has(selectedTab)) {
            // Update ref (doesn't cause re-render)
            hasScrolledRef.current.add(selectedTab);

            // Update state for checkbox rendering only
            setHasScrolledToBottom(prevSet => {
                const newSet = new Set(prevSet);
                newSet.add(selectedTab);
                return newSet;
            });
        }
    };

    useEffect(() => {
        // Only check scroll position after PDF has loaded
        if (!open || !selectedTab || !numPages) return;

        const timer = setTimeout(() => {
            if (contentRef.current) {
                const element = contentRef.current;
                const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;

                // Only update if not already marked as scrolled
                if (isAtBottom && !hasScrolledRef.current.has(selectedTab)) {
                    hasScrolledRef.current.add(selectedTab);
                    setHasScrolledToBottom(prevSet => {
                        const newSet = new Set(prevSet);
                        newSet.add(selectedTab);
                        return newSet;
                    });
                }
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [selectedTab, open, numPages]);

    // Add mouse wheel scroll support for tabs
    useEffect(() => {
        const tabsElement = tabsRef.current;
        if (!tabsElement) return;

        const handleWheel = (e) => {
            const scrollContainer = tabsElement.querySelector('.MuiTabs-scroller');
            if (scrollContainer) {
                e.preventDefault();
                scrollContainer.scrollLeft += e.deltaY;
            }
        };

        tabsElement.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            tabsElement.removeEventListener('wheel', handleWheel);
        };
    }, []);

    // Update PDF width on window resize for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            setPdfWidth(calcPdfWidth());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <Dialog
            open={open}
            onClose={isDismissable ? () => onDismiss() : undefined}
            disableEscapeKeyDown={!isDismissable}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: { xs: 'calc(100vh - 32px)', sm: '70vh' },
                    borderRadius: "12px",
                    backgroundColor: 'var(--neutral-3)',
                    m: 0,
                    maxHeight: { xs: 'calc(100vh - 32px)', sm: '70vh' },
                    width: { xs: 'calc(100vw - 32px)', sm: '100%' },
                    overflowX: 'hidden'
                }
            }}
            sx={{
                '& .MuiDialog-container': {
                    alignItems: 'center',
                    justifyContent: 'center'
                },
                '& .MuiDialog-paper': {
                    overflowX: 'hidden'
                }
            }}
        >
            <DialogTitle sx={{ padding: { xs: 2, sm: 3 }, overflow: 'hidden', position: 'relative' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            backgroundColor: "#202939",
                            width: "48px",
                            height: "48px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "24px",
                            flexShrink: 0,
                            mt: { xs: '8px', sm: 0 }
                        }}>
                            <FileText />
                        </Box>
                        <Typography sx={{
                            fontWeight: "600",
                            fontSize: "18px",
                            zIndex: 1,
                            mt: { xs: '8px', sm: 0 }
                        }}>
                            Terms and conditions
                        </Typography>
                    </Box>
                    {isDismissable && (
                        <IconButton aria-label="Close" onClick={() => onDismiss()} sx={{ color: '#70707B' }}>
                            <X size={24} />
                        </IconButton>
                    )}
                </Box>
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: 480,
                        height: 480
                    }}
                >
                    <svg
                        width="240"
                        height="240"
                        viewBox="0 0 240 240"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="48" cy="48" r="48" stroke="#202939" />
                        <circle cx="48" cy="48" r="80" stroke="#202939" />
                        <circle cx="48" cy="48" r="112" stroke="#202939" opacity={0.6} />
                        <circle cx="48" cy="48" r="144" stroke="#202939" opacity={0.4} />
                        <circle cx="48" cy="48" r="176" stroke="#202939" opacity={0.2} />
                    </svg>
                </div>
                {ownerRoleGroups.length > 1 && <Box ref={tabsRef} sx={{ position: 'relative', zIndex: 2, mt: '12px', display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    <Tabs
                        value={selectedTab}
                        onChange={handleTabsChange}
                        variant="scrollable"
                        className={classes.selectedTab}
                        scrollButtons={false}
                        indicatorColor='transparent'
                    >
                        {
                            ownerRoleGroups.map((groupItem, index) => {
                                const tosIsValid = isTosValid(groupItem.group.accepted_tos_version);
                                const hasAgreed = agreedToTerms.has(groupItem.group.name);
                                const isDisabled = tosIsValid || hasAgreed;
                                const showAlert = !tosIsValid && !hasAgreed;
                                const isSelected = selectedTab === groupItem.group.name;
                                return (
                                    <Tab
                                        disabled={isDisabled}
                                        key={groupItem.group.id}
                                        value={groupItem.group.name}
                                        label={<span style={{ opacity: isDisabled ? 0.4 : 1 }}>{groupItem.group.name}</span>}
                                        icon={<span style={{ opacity: isDisabled ? 0.4 : 1 }}>{showAlert ? <CircleAlert color="var(--color-text-danger)" /> : <CircleCheck color="var(--color-text-success)" />}</span>}
                                        iconPosition="end"
                                        className={classes.reportFilterButton}
                                        style={{
                                            ...(index === 0
                                                ? { borderRadius: 'var(--radius-l) 0 0 var(--radius-l)' }
                                                : index === ownerRoleGroups.length - 1 ? { borderRadius: '0 var(--radius-l) var(--radius-l) 0', borderLeft: '0px solid var(--color-border)' }
                                                    : { borderLeft: '0px solid var(--color-border)' }
                                            ),
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            backgroundColor: isSelected ? 'var(--Colors-Background-bg-active, #1F242F)' : 'var(--color-surface)',
                                            position: 'relative',
                                            zIndex: 2
                                        }}
                                    />
                                )
                            })
                        }
                    </Tabs>
                </Box>}
            </DialogTitle>


            <DialogContent
                sx={{
                    backgroundColor: '#fff',
                    color: "#000",
                    p: 2,
                    mx: { xs: 1, sm: 6 },
                    my: 0,
                    zIndex: 2,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    maxWidth: '100%',
                    position: 'relative'
                }}
                ref={contentRef}
                onScroll={handleScroll}
            >
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    '& .textLayer': {
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        pointerEvents: 'auto',
                        touchAction: 'manipulation',
                    },
                    '& .textLayer span': {
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        pointerEvents: 'auto',
                        cursor: 'text',
                    },
                }}>
                <Document
                    file={pdfFile}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    }
                    error={
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', color: 'red', flexDirection: 'column', gap: 2 }}>
                            <Typography>Failed to load Terms of Service</Typography>
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#999' }}>
                                Please refresh the page or contact support
                            </Typography>
                        </Box>
                    }
                    options={pdfOptions}
                >
                    {numPages && Array.from(new Array(numPages), (el, index) => (
                        <Page
                            key={`page_${index + 1}`}
                            pageNumber={index + 1}
                            renderTextLayer={true}
                            renderAnnotationLayer={true}
                            onRenderTextLayerSuccess={() => processPageLinks(index + 1)}
                            width={pdfWidth}
                            loading={
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '600px' }}>
                                    <CircularProgress size={24} />
                                </Box>
                            }
                        />
                    ))}
                </Document>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                backgroundColor: 'var(--neutral-3)',
                p: { xs: '16px', sm: '24px' },
                m: 0,
                justifyContent: "space-between",
                minHeight: '80px',
                alignItems: 'flex-start',
                flexDirection: 'column',
                gap: 2,
                overflowX: 'hidden',
                maxWidth: '100%',
                '& > :not(style) + :not(style)': {
                    ml: 0
                }
            }}>
                <FormControlLabel
                    sx={{
                        width: '100%',
                        color: hasScrolledToBottom.has(selectedTab) || agreedToTerms.has(selectedTab) ? "inherit" : "var(--color-text-danger)",
                        minHeight: '48px',
                        m: 0,
                        alignItems: 'center'  // Center checkbox vertically with label
                    }}
                    control={
                        <Checkbox
                            checked={boxChecked.has(selectedTab)}
                            onChange={() => handleToggleSet(selectedTab)}
                            disabled={!hasScrolledToBottom.has(selectedTab) && !agreedToTerms.has(selectedTab)}
                            sx={{
                                alignSelf: hasScrolledToBottom.has(selectedTab) || agreedToTerms.has(selectedTab) ? 'flex-start' : 'center',  // Align to first line for multi-line, center for single line
                                mt: hasScrolledToBottom.has(selectedTab) || agreedToTerms.has(selectedTab) ? '-9px' : '0px'  // Only apply negative margin for multi-line text
                            }}
                        />
                    }
                    label={hasScrolledToBottom.has(selectedTab) || agreedToTerms.has(selectedTab) ? "By checking this box, I confirm that I have read the Terms of Service. I represent and warrant that I am authorized to accept these terms on behalf of the company, organization, or other entity I represent, and that such entity will be bound by these terms." : "Please scroll to bottom"}
                />
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    width: '100%',
                    justifyContent: 'flex-end',
                    pr: { xs: 0, sm: '16px' }
                }}>
                    <Button
                        onClick={handleLogout}
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: "600",
                            lineHeight: "20px",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                        }}
                        style={{
                            borderRadius: "var(--radius-xs, 4px)",
                            border: "1px solid var(--Component-colors-Components-Buttons-Secondary-button-secondary-border, #364152)",
                            background: "var(--Component-colors-Components-Buttons-Secondary-button-secondary-bg, #121926)",
                            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                            color: "#e6e5e8",
                            minWidth: 85,
                        }}
                        variant="contained"
                    >
                        Logout
                    </Button>
                    <Button
                        component="a"
                        href={tosDocument}
                        download="Foundry_USA_Pool_Service_Agreement.pdf"
                        sx={{
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: "600",
                            lineHeight: "20px",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            '& .arrowLeft, & .arrowRight': {
                                transform: 'translate(0px, 0px)',
                                transition: 'all 0.5s ease',
                            },
                            '&:hover .arrowLeft, &:hover .arrowRight': {
                                transform: 'translate(0px, 3px)',
                                transition: 'all 0.5s ease',
                            },
                        }}
                        style={{
                            borderRadius: "var(--radius-xs, 4px)",
                            border: "1px solid var(--Component-colors-Components-Buttons-Secondary-button-secondary-border, #364152)",
                            background: "var(--Component-colors-Components-Buttons-Secondary-button-secondary-bg, #121926)",
                            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                            color: "#e6e5e8",
                            minWidth: 85,
                        }}
                        variant="contained"
                    >
                        <SvgIcon sx={{ width: '20px', height: '20px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path className="arrowLeft" d="M12 13v8l-4-4" />
                                <path className="arrowRight" d="m12 21 4-4" />
                                <path d="M4.393 15.269A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.436 8.284" />
                            </svg>
                        </SvgIcon>
                        Download
                    </Button>
                    <Button
                        disabled={!boxChecked.has(selectedTab) || agreedToTerms.has(selectedTab) || isSubmitting}
                        sx={{
                            opacity: boxChecked.has(selectedTab) && !agreedToTerms.has(selectedTab) && !isSubmitting ? 1 : 0.2,
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: "600",
                            lineHeight: "20px",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                        }}
                        style={{
                            borderRadius: "var(--radius-xs, 4px)",
                            border: "1px solid #00B161",
                            background: "#00B161",
                            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                            color: "#e6e5e8",
                            minWidth: 85,
                        }}
                        onClick={() => handleAgreeClick(selectedTab)}
                        variant="contained"
                    >
                        I agree
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

TermsOfServiceDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onAllAccepted: PropTypes.func.isRequired,
    isDismissable: PropTypes.bool,
    onDismiss: PropTypes.func,
};

export default TermsOfServiceDialog;